import React,{useState, useRef, useEffect, useContext} from 'react'
import {SplitText} from "@cyriacbr/react-split-text" //Splitting tool mandatory for this component
import gsap from 'gsap'
import './titleAnimated.css'

function TitleAnimatedRotated(props) {
  var letterSpeed = props.letterSpeed;
  const CustomLevel = `h${props.level}`;
  const [currentTitle, setCurrentTitle] = useState(props.title[0]);
  const [hasStarted, setHasStarted] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const currentIndexRef = useRef(0);
  const rotationTimeoutRef = useRef(null);
  const observerRef = useRef(null);

  function showNext(element){
    gsap.to(element.nextSibling, 3, {delay: 2, opacity: 1, ease: 'expo'});
  }
  
  function titleEnterEffects(element){
    return new Promise((resolve) => {
      var _letters = element.querySelectorAll('span');
      var _index = 0;
      
      if (_letters.length === 0) {
        resolve("success");
        return;
      }
      
      _letters.forEach((_letter, i) => {
        _index += letterSpeed + _index/100;

        gsap.to(_letter, _index, {
          delay: 0,
          opacity: 1,
          onComplete: () => {
            _letter.classList.add('pageTitleRotated', 'glowTitle');
            if (i === _letters.length - 1) {
              resolve("success");
            }
          }
        });
      });
    });
  }

  function titleExitEffects(element) {
    return new Promise((resolve) => {
      var _letters = element.querySelectorAll('span');
      
      if (_letters.length === 0) {
        resolve();
        return;
      }
      
      // Animate letters out one by one (reverse order for nice effect)
      gsap.to(_letters, {
        duration: 0.05,
        opacity: 0,
        stagger: -0.03, // negative stagger = reverse order
        ease: 'power2.inOut',
        onComplete: () => {
          _letters.forEach(letter => {
            letter.classList.remove('pageTitle', 'glowTitle');
          });
          resolve();
        }
      });
    });
  }

  function fadeOutSVG() {
    if (svgRef.current) {
      gsap.to(svgRef.current, {
        duration: 0.8,
        opacity: 0,
        ease: 'power2.inOut'
      });
    }
  }

  function fadeInSVG() {
    if (svgRef.current) {
      gsap.to(svgRef.current, {
        duration: 0.8,
        opacity: 1,
        ease: 'power2.inOut'
      });
    }
  }

  function stopRotation() {
    if (rotationTimeoutRef.current) {
      clearTimeout(rotationTimeoutRef.current);
      rotationTimeoutRef.current = null;
    }
  }

  function hideComponent() {
    setIsVisible(false);
    // Fade out the entire container
    if (containerRef.current) {
      gsap.to(containerRef.current, {
        duration: 0.8,
        opacity: 0,
        ease: 'power2.inOut'
      });
    }
  }

  function showComponent() {
    setIsVisible(true);
    // Fade in the entire container
    if (containerRef.current) {
      gsap.to(containerRef.current, {
        duration: 0.8,
        opacity: 1,
        ease: 'power2.inOut'
      });
    }
  }

  function rotateToNextTitle() {
    // Stop if we've reached the last title
    if (currentIndexRef.current >= props.title.length - 1) {
      ////console.log('Reached last title, stopping rotation');
      
      // Hide SVG and eventually the whole component when reaching empty title
      fadeOutSVG();
      
      // If the last title is empty, hide the component after a delay
      if (props.title[props.title.length - 1] === '') {
        setTimeout(() => {
          hideComponent();
        }, 1000);
      }
      
      if (props.afterTitle) {
        showNext(containerRef.current);
      }
      return;
    }

    ////console.log('rotateToNextTitle called, currentIndex:', currentIndexRef.current, 'total titles:', props.title.length);
    
    const nextIndex = currentIndexRef.current + 1;
    const nextTitle = props.title[nextIndex];
    
    ////console.log('Moving to index:', nextIndex, 'title:', nextTitle);
    
    // If next title is empty, fade out SVG
    if (nextTitle === '') {
      fadeOutSVG();
    }
    
    // Exit current title
    titleExitEffects(containerRef.current).then(() => {
      // Update the title text and index
      setCurrentTitle(nextTitle);
      currentIndexRef.current = nextIndex;
      
      // Wait a moment for React to update the DOM with new text
      setTimeout(() => {
        // Only animate if title is not empty
        if (nextTitle !== '') {
          // Enter new title
          titleEnterEffects(containerRef.current).then(() => {
            ////console.log('Finished animating title at index:', nextIndex);
            
            // Continue to next title after 2 seconds if not the last one
            if (nextIndex < props.title.length - 1) {
              rotationTimeoutRef.current = setTimeout(() => {
                rotateToNextTitle();
              }, 2000);
            } else {
              ////console.log('This was the last title, stopping');
              if (props.afterTitle) {
                showNext(containerRef.current);
              }
            }
          });
        } else {
          // If empty title, just wait and potentially hide
          setTimeout(() => {
            hideComponent();
          }, 1000);
        }
      }, 100);
    });
  }

  // Observer for title effects
  let optionsTitle = {
    rootMargin: '0px 0px 0px 0px',
    threshold: 0,
  };
  
  let callbackTitle = (entries, observerTitle) => {
    entries.forEach((entry) => {
      if(entry.isIntersecting && !hasStarted){
        setHasStarted(true);

        setTimeout(() => {
          titleEnterEffects(containerRef.current).then(() => {
            // Start rotation if there are multiple titles
            if (props.title.length > 1) {
              rotationTimeoutRef.current = setTimeout(() => {
                rotateToNextTitle();
              }, 2000); // Wait 2 seconds before starting rotation
            } else if (props.afterTitle) {
              showNext(containerRef.current);
            }
          });
        }, 400);
      }
    });
  };

  useEffect(() => {
    observerRef.current = new IntersectionObserver(callbackTitle, optionsTitle);
    
    if (containerRef.current) {
      observerRef.current.observe(containerRef.current);
    }

    return () => {
      // Clean up timeout and observer when component unmounts
      stopRotation();
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Re-observe when title changes to ensure DOM is ready
  useEffect(() => {
    if (containerRef.current && hasStarted && currentIndexRef.current > 0) {
      // Small delay to ensure SplitText has rendered new spans
      setTimeout(() => {
        // This effect runs after title change, DOM should be ready
      }, 50);
    }
  }, [currentTitle]);

  // Expose methods for external control
  React.useImperativeHandle(props.forwardedRef, () => ({
    stopRotation,
    hideComponent,
    showComponent,
    fadeOutSVG,
    fadeInSVG
  }));

  return (
    <CustomLevel 
      ref={containerRef}
      className={`${props.classes} proj-title-rotated relative h-[76px] md:h-[110px] pl-[70px] ${!isVisible ? 'fadeOut' : ''}`} 
      style={{
        transform:'scale('+props.scale+')',
        transformOrigin:'bottom left', 
        color:props.color,
      }}
    >
      <svg 
        ref={svgRef}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 transition-opacity duration-800 ease-in-out" 
        width="60" 
        xmlns="http://www.w3.org/2000/svg" 
        version="1.1" 
        fill="#fff" 
        viewBox="0 0 140 100"
      >
        <path d="M52.35,17.79c-.41-1.45,1.6-10.97,3.37-10.53,2.57.64,4.1,8.98,4.53,11.31,1.68.21,3.9.64,5.18,1.82,1.1,1.01,1.21,2.53,2.02,3.46,2.44,2.81,10.68,2.35,11.72,3.8,1.9,2.67-3.88,8.63-6.42,9.76-1.35.6-7.65,1.85-7.87,2.48-.59,1.74.93,3.91,1.39,5.53,2.68,9.36,1.61,18.24-4.79,25.77-1.18,1.39-3.49,2.79-3.66,4.56-.14,1.42-.15,10.56.22,11.37l.33.36c3.8.5,6.35,1.81,6.36,6.01,0,.64-.15.99-.78,1.17-1.35.38-8.58.32-10.22.13-.6-.07-1.12-.18-1.65-.48-2.23-1.26-6.36-13.03-7.2-13.21l-9.23,5.37.1,1.04c2.36.31,7.37,4.24,6.52,6.79l-.46.46c-11.39-.09-22.84.46-34.19-.42-8.18-7.52-4.54-20.6,1.53-28.29,6.63-8.41,15.27-12.23,23.27-18.42,5.29-4.1,5.07-7.99,6.45-13.94.25-1.09,1.81-5.59,1.8-5.99-.02-.63-1.6-4.1-1.92-5.26-.94-3.37-2.58-13.25-.41-15.98.86-1.08,2.67.07,3.52.7,2.51,1.85,6.01,6.54,8.36,8.99.28.29,1.83,2.01,2.14,1.66ZM62.85,26c0-.84-.68-1.52-1.52-1.52s-1.52.68-1.52,1.52.68,1.52,1.52,1.52,1.52-.68,1.52-1.52Z"/>
        <path d="M123.29,68.55c0,14.42-11.69,26.11-26.11,26.11s-26.11-11.69-26.11-26.11,11.69-26.11,26.11-26.11,26.11,11.69,26.11,26.11ZM102.66,52.89c0-2.97-2.4-5.37-5.37-5.37s-5.37,2.4-5.37,5.37,2.4,5.37,5.37,5.37,5.37-2.4,5.37-5.37ZM81.18,58.03c-1.18.24-2.56,1.61-3.13,2.65-2.74,5.06,3.22,9.99,7.62,6.71s1.53-10.6-4.49-9.36ZM117.14,63.13c0-2.89-2.34-5.23-5.23-5.23s-5.23,2.34-5.23,5.23,2.34,5.23,5.23,5.23,5.23-2.34,5.23-5.23ZM100.1,68.32c0-1.57-1.27-2.84-2.84-2.84s-2.84,1.27-2.84,2.84,1.27,2.84,2.84,2.84,2.84-1.27,2.84-2.84ZM93.39,80.37c0-2.96-2.4-5.36-5.36-5.36s-5.36,2.4-5.36,5.36,2.4,5.36,5.36,5.36,5.36-2.4,5.36-5.36ZM111.51,80.4c0-2.92-2.37-5.29-5.29-5.29s-5.29,2.37-5.29,5.29,2.37,5.29,5.29,5.29,5.29-2.37,5.29-5.29Z"/>
        <path d="M136.96,85.05v8.83c-7.91.53-16.02.01-23.74-1.83,2.84-2.21,5.16-4.79,7.45-7.55,4.77,2.82,11.07,1.07,16.29.56Z"/>
      </svg>
      
      {currentTitle && (
        <SplitText 
          className="ttl"
          LineWrapper={({ wordIndex, children }) => (
            <span className="ttl_wrapper">
              {children}
            </span>
          )}
        >
          {currentTitle}
        </SplitText>
      )}
    </CustomLevel>
  )
}

export default TitleAnimatedRotated